import { useEffect, useRef } from 'react';

/**
 * 중첩 모달의 "뒤로가기 순차 닫기"를 위한 공유 스택.
 *
 * 모달마다 popstate 리스너를 각자 달면, 뒤로가기 한 번에 모든 리스너가 동시에 발동해
 * 모든 모달이 닫혀버린다. 그래서 단일 리스너 + 스택으로 "맨 위 모달 하나만" 닫는다.
 */
const modalStack = [];
let isListening = false;
// 버튼 등으로 닫을 때 정리용으로 호출하는 history.back()이 일으키는 popstate는 무시해야 한다.
let ignorePopCount = 0;
let idCounter = 0;

function handlePopState() {
    if (ignorePopCount > 0) {
        ignorePopCount--;
        return;
    }
    // 실제 뒤로가기: 맨 위 모달 하나만 닫는다.
    const top = modalStack.pop();
    if (top) {
        top.close();
    }
}

function ensureListening() {
    if (!isListening) {
        window.addEventListener('popstate', handlePopState);
        isListening = true;
    }
}

function openModal(id, close) {
    ensureListening();
    modalStack.push({ id, close });
    // URL은 유지한 채 history 항목만 하나 추가
    window.history.pushState({ __modalId: id }, '');
}

function closeModal(id) {
    const idx = modalStack.findIndex((e) => e.id === id);
    if (idx === -1) {
        // 이미 뒤로가기로 닫힌 경우 → 정리 불필요
        return;
    }
    modalStack.splice(idx, 1);
    // 버튼 등으로 닫힌 경우: push 했던 history 항목을 제거 (popstate는 무시)
    ignorePopCount++;
    window.history.back();
}

/**
 * 모바일/브라우저의 "뒤로가기"로 모달을 닫는다. 중첩 시 맨 위부터 순차적으로 닫힌다.
 * URL은 바꾸지 않으므로 라우팅에는 영향이 없다.
 *
 * @param {boolean} open
 * @param {function} onClose
 */
export function useModalBackClose(open, onClose) {
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onCloseRef.current = onClose;
    });

    useEffect(() => {
        if (!open) {
            return;
        }

        const id = ++idCounter;
        openModal(id, () => onCloseRef.current?.());

        return () => {
            closeModal(id);
        };
    }, [open]);
}
